import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  useAddNewDealerMutation,
  useGenerateComponentFileMutation,
  useGetOneDealerQuery,
  useUpdateDealerMutation,
  useDownloadComponentFileMutation,
  IDealerDownloadComponentFile,
} from 'thunks/RTKQuery/dealer';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { confirmationSlice, toastSlice } from 'slices';
import { Spinner2 } from 'components/Spinner2';
import { isFetchBaseQueryError } from 'utils/helpers';
import { useTranslation } from 'react-i18next';
import { dealerInitialState } from 'slices/dealer-details';
import { CONFIGURATION_TYPE } from 'constants/constants';
import { isValidJson } from 'utils/common';
import { FormikHelpers } from 'formik';
import i18n from 'translations';
import { IDealerInfo } from 'slices/auth';
import { Confirmation } from 'components/Modal';
import {
  IDealerItem,
  IDealerObject,
  IValidationDealer,
} from '../Models/dealers';
import { DealersDetailView } from './DealersDetailView';

interface IDealerDetailContainer {
  setErrorString: (err: string) => void;
  selectedDealer?: IDealerItem;
  rootDealer?: IDealerInfo;
  setSelectedDealer: (dealer: IDealerItem) => void;
  appendListViewData: (data: IDealerItem) => void;
}

const isConfigurationJSON = (config: any) => {
  return config === CONFIGURATION_TYPE.FIXED_JSON;
};

const schema: yup.SchemaOf<IValidationDealer> = yup.object().shape({
  dealerId: yup.string().required(i18n.t('dealerManagement.requiredDealerId')),
  name: yup.string().required(i18n.t('dealerManagement.requiredName')),
  configuration: yup.object().shape({
    configurationType: yup
      .string()
      .required(i18n.t('dealerManagement.requiredConfigType'))
      .nullable(),
    fixedConfigJson: yup.string().when('configurationType', {
      is: (val: any) => isConfigurationJSON(val),
      then: yup
        .string()
        .test('is-json', i18n.t('dealerManagement.invalidJson'), (value) => {
          return (
            isValidJson(value) ||
            new yup.ValidationError(
              i18n.t('dealerManagement.invalidJson'),
              value,
              'fixedConfigJson'
            )
          );
        }),
    }),
  }),
  parentDealerId: yup.string().optional(),
  email: yup
    .string()
    .nullable()
    .when('parentDealerId', {
      is: (val: string) => val === '' || val === undefined || val === null,
      then: yup
        .string()
        .email()
        .required(i18n.t('dealerManagement.requiredParentEmail'))
        .nullable(),
      otherwise: yup
        .string()
        .email(i18n.t('dealerManagement.emailNotValid'))
        .optional()
        .nullable(),
    }),
});

export const DealersDetailViewContainer = ({
  setErrorString,
  selectedDealer,
  setSelectedDealer,
  appendListViewData,
  rootDealer,
}: IDealerDetailContainer) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [dealerData, setDealerData] = useState<IDealerObject>({
    ...dealerInitialState.data,
    parentDealerId: rootDealer?.dealerId ?? '',
    parentDealerName: rootDealer?.name ?? '',
  });

  const { show: showConfirmation } = useAppSelector(
    (state) => state.confirmation
  );
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const {
    data: detailData,
    isLoading: dealerDetailLoading,
    error: dealerDetailError,
    refetch: refetchDealerDetail,
  } = useGetOneDealerQuery(selectedDealer?.id ?? 0, {
    skip: !selectedDealer?.id,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!detailData || !detailData?.id) return;
    const fixedConfigJson =
      detailData && detailData.configuration.fixedConfigJson
        ? JSON.stringify(detailData.configuration.fixedConfigJson)
        : '';
    const config = { ...detailData.configuration, fixedConfigJson };
    setDealerData({ ...detailData, configuration: config });
  }, [detailData]);

  useEffect(() => {
    if (!selectedDealer) {
      setDealerData({
        ...dealerInitialState.data,
        parentDealerId: rootDealer?.dealerId ?? '',
        parentDealerName: rootDealer?.name ?? '',
        configuration: {
          ...dealerInitialState.data.configuration,
          permissions: rootDealer?.permissions,
        },
      });
    } else {
      refetchDealerDetail();
    }
  }, [selectedDealer, refetchDealerDetail]);

  const [
    addNewDealer,
    {
      isLoading: dealerSaveLoading,
      error: dealerSaveError,
      status: dealerSaveStatus,
    },
  ] = useAddNewDealerMutation();
  const [
    updateDealer,
    {
      isLoading: dealerUpdateLoading,
      error: dealerUpdateError,
      status: dealerUpdateStatus,
    },
  ] = useUpdateDealerMutation();
  const [
    generateComponentFile,
    {
      isLoading: generateComponentFileLoading,
      status: generateComponentFileStatus,
      error: generateComponentFileError,
    },
  ] = useGenerateComponentFileMutation();

  const [
    downloadComponentFile,
    {
      isLoading: componentFileDownloadLoading,
      error: componentFileDownloadError,
    },
  ] = useDownloadComponentFileMutation();

  const handleDownloadComponentFile = async (dealerId: string) => {
    setIsProcessing(true);
    const res = await downloadComponentFile(dealerId);
    if ('error' in res && res.error) {
      setErrorString(`${(res.error as any).data?.message?.errors ?? ''}`);
    } else if ('data' in res) {
      const downloadData = (res.data as unknown) as IDealerDownloadComponentFile;
      window.location.href = downloadData.downloadUrl;
    }

    setIsProcessing(false);
  };

  const handleGenerateComponentFile = async (
    baseUrl: string,
    dealerId: string
  ) => {
    setIsProcessing(true);
    const res = await generateComponentFile({
      baseUrl,
      dealerId,
    });
    if ('error' in res && res.error) {
      setErrorString(`${(res.error as any).data?.message?.errors ?? ''}`);
    }
    setIsProcessing(false);
  };

  useEffect(() => {
    if (dealerDetailError && 'message' in dealerDetailError) {
      setErrorString(`${dealerDetailError.message}`);
    }
    if (dealerSaveError && isFetchBaseQueryError(dealerSaveError)) {
      const msg = dealerSaveError as any;
      setErrorString(`${msg.data.message.errors}`);
    }
    if (dealerUpdateError && isFetchBaseQueryError(dealerUpdateError)) {
      const msg = dealerUpdateError as any;
      setErrorString(`${msg.data.message.errors}`);
    }
    if (
      generateComponentFileError &&
      isFetchBaseQueryError(generateComponentFileError)
    ) {
      const msg = generateComponentFileError as any;
      setErrorString(`${msg.data.message.errors}`);
    }
    if (
      componentFileDownloadError &&
      isFetchBaseQueryError(componentFileDownloadError)
    ) {
      const msg = componentFileDownloadError as any;
      setErrorString(`${msg.data.message.errors}`);
    }
  }, [
    dealerDetailError,
    dealerSaveError,
    dealerUpdateError,
    generateComponentFileError,
    componentFileDownloadError,
  ]);

  const handleSaveDealerResponse = async (res: any) => {
    if (!res?.error) {
      const dealerObj = res.data as IDealerItem;
      setSelectedDealer(dealerObj);
      appendListViewData(dealerObj);
    }
    setIsProcessing(false);
  };
  const updateDealerDetails = async (data: IDealerObject) => {
    setIsProcessing(true);
    const res = (await updateDealer(data)) as any;
    handleSaveDealerResponse(res);
  };

  const handleSubmit = async (
    values: IDealerObject,
    formik: FormikHelpers<IDealerObject>
  ) => {
    setErrorString('');
    if (!selectedDealer?.id) {
      setIsProcessing(true);
      const res = await addNewDealer(values);
      handleSaveDealerResponse(res);
    } else if (selectedDealer.userId && selectedDealer.email !== values.email) {
      dispatch(
        confirmationSlice.actions.show({
          title: i18n.t('dealerManagement.updateConfirm.title'),
          message: i18n.t('dealerManagement.updateConfirm.message'),
          buttonOk: i18n.t('dealerManagement.updateConfirm.buttonOk'),
          buttonCancel: i18n.t('dealerManagement.updateConfirm.buttonCancel'),
          callback: async () => {
            dispatch(confirmationSlice.actions.close());
            await updateDealerDetails(values);
          },
        })
      );
    } else {
      await updateDealerDetails(values);
    }

    formik.resetForm({ values: { ...values } });
  };

  useEffect(() => {
    if (dealerSaveStatus === QueryStatus.fulfilled && !dealerSaveError) {
      dispatch(
        toastSlice.actions.show({
          title: t('dealer.save.title'),
          message: t('dealer.save.message'),
        })
      );
    }
  }, [dealerSaveStatus]);

  useEffect(() => {
    if (dealerUpdateStatus === QueryStatus.fulfilled && !dealerUpdateError) {
      dispatch(
        toastSlice.actions.show({
          title: t('dealer.update.title'),
          message: t('dealer.update.message'),
        })
      );
    }
  }, [dealerUpdateStatus]);

  useEffect(() => {
    if (
      generateComponentFileStatus === QueryStatus.fulfilled &&
      !generateComponentFileError
    ) {
      dispatch(
        toastSlice.actions.show({
          title: t('dealer.generate.title'),
          message: t('dealer.generate.message'),
        })
      );
    }
  }, [generateComponentFileStatus]);

  return (
    <>
      <Spinner2
        show={
          dealerDetailLoading ||
          dealerUpdateLoading ||
          dealerSaveLoading ||
          generateComponentFileLoading ||
          componentFileDownloadLoading ||
          isProcessing
        }
      />
      <DealersDetailView
        detailData={dealerData}
        validationSchema={schema}
        onSubmit={handleSubmit}
        rootDealer={rootDealer}
        generateComponentFile={handleGenerateComponentFile}
        downloadComponentFile={handleDownloadComponentFile}
      />
      {showConfirmation && <Confirmation />}
    </>
  );
};

DealersDetailViewContainer.defaultProps = {
  selectedDealer: undefined,
  rootDealer: undefined,
};
